import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  TrendingUp, 
  Target, 
  Zap, 
  BarChart3, 
  Users, 
  Award, 
  CheckCircle, 
  Sparkles,
  Eye,
  Heart,
  Shield
} from 'lucide-react';
import './about.css';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const statsRef = useRef(null);
  const valuesRef = useRef(null);
  const teamRef = useRef(null);
  const orbsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Orbs animation
      orbsRef.current.forEach((orb, index) => {
        if (orb) {
          gsap.to(orb, {
            rotation: 360,
            duration: 20 + index * 5,
            repeat: -1,
            ease: "none"
          });
          
          gsap.to(orb, {
            x: Math.sin(index * 60) * 60,
            y: Math.cos(index * 60) * 40,
            duration: 10 + index * 2,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut"
          });
        }
      });

      // Header animation
      if (headerRef.current) {
        gsap.fromTo(headerRef.current.children, 
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            stagger: 0.2,
            scrollTrigger: {
              trigger: headerRef.current,
              start: "top 80%"
            }
          }
        );
      }

      // Stats counter animation
      if (statsRef.current) {
        gsap.fromTo(statsRef.current.querySelectorAll('.stat-number'),
          { innerText: 0 },
          {
            innerText: (i, el) => el.getAttribute('data-value'),
            duration: 2,
            snap: { innerText: 1 },
            scrollTrigger: {
              trigger: statsRef.current,
              start: "top 80%"
            }
          }
        );

        gsap.fromTo(statsRef.current.children,
          { opacity: 0, y: 50, scale: 0.8 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.8,
            stagger: 0.2,
            scrollTrigger: {
              trigger: statsRef.current,
              start: "top 80%"
            }
          }
        );
      }

      // Values animation
      if (valuesRef.current) {
        gsap.fromTo(valuesRef.current.children,
          { opacity: 0, x: -50, rotation: -5 },
          {
            opacity: 1,
            x: 0,
            rotation: 0,
            duration: 1,
            stagger: 0.2,
            scrollTrigger: {
              trigger: valuesRef.current,
              start: "top 80%"
            }
          }
        );
      }

      // Team section animation
      if (teamRef.current) {
        gsap.fromTo(teamRef.current.children,
          { opacity: 0, scale: 0.8 },
          {
            opacity: 1,
            scale: 1,
            duration: 0.8,
            stagger: 0.15,
            scrollTrigger: {
              trigger: teamRef.current,
              start: "top 80%"
            }
          }
        );
      }

      // Floating particles animation
      gsap.to(".floating-particles", {
        backgroundPosition: "0% -100%",
        duration: 30,
        repeat: -1,
        ease: "none"
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="about-section">
      <div className="about-background">
        <div className="gradient-mesh">
          <div ref={el => orbsRef.current[0] = el} className="mesh-gradient mesh-1"></div>
          <div ref={el => orbsRef.current[1] = el} className="mesh-gradient mesh-2"></div>
          <div ref={el => orbsRef.current[2] = el} className="mesh-gradient mesh-3"></div>
          <div ref={el => orbsRef.current[3] = el} className="mesh-gradient mesh-4"></div>
          <div ref={el => orbsRef.current[4] = el} className="mesh-gradient mesh-5"></div>
        </div>
        <div className="grid-pattern"></div>
        <div className="floating-particles"></div>
        <div className="noise-texture"></div>
      </div>

      <div className="about-container">
        {/* Header Section */}
        <div ref={headerRef} className="about-header">
          <div className="achievement-badge">
            <Sparkles className="badge-icon" />
            <span className="badge-text">About Beyond the Analysis</span>
          </div>
          <h1 className="about-title">
            <span className="animate-word">The Story Behind</span>
            <span className="highlight-word">the Data</span>
          </h1>
          <p className="about-subtitle">
            Two marketers. One frustration. A shift in the making.
          </p>
        </div>

        {/* Stats Section */}
        <div ref={statsRef} className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <TrendingUp />
            </div>
            <div className="stat-content">
              <div className="stat-number" data-value="250">0</div>
              <div className="stat-label">Projects Completed</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <Users />
            </div>
            <div className="stat-content">
              <div className="stat-number" data-value="150">0</div>
              <div className="stat-label">Happy Clients</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <BarChart3 />
            </div>
            <div className="stat-content">
              <div className="stat-number" data-value="85">0</div>
              <div className="stat-label">Average ROI Increase (%)</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <Award />
            </div>
            <div className="stat-content">
              <div className="stat-number" data-value="5">0</div>
              <div className="stat-label">Years of Experience</div>
            </div>
          </div>
        </div>

        {/* Mission Section */}
        <div className="mission-section">
          <div className="section-header">
            <div className="section-badge">
              <Target className="badge-icon" />
              <span className="badge-text">Our Story</span>
            </div>
            <h2 className="section-title">
              The Foundation of <span className="highlight">Our Mission</span>
            </h2>
          </div>
          
          <div className="mission-content">
            <div className="mission-text">
              <p>
                Founders were tired of watching businesses burn through marketing budgets with little to show for it. Too often, agencies chased vanity metrics—delivering polished reports that looked good on paper but made no real difference to growth. Data was treated like decoration instead of direction.
              </p>
              <p>
                So they asked a better question: What if marketing worked the way it should—delivering real results, not just numbers?
              </p>
            </div>
            
            <div className="mission-highlights">
              <div className="highlight-item">
                <CheckCircle className="highlight-icon" />
                <span>Real results over vanity metrics</span>
              </div>
              <div className="highlight-item">
                <CheckCircle className="highlight-icon" />
                <span>Data as direction, not decoration</span>
              </div>
              <div className="highlight-item">
                <CheckCircle className="highlight-icon" />
                <span>Marketing that actually works</span>
              </div>
              <div className="highlight-item">
                <CheckCircle className="highlight-icon" />
                <span>Growth-focused strategies</span>
              </div>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="values-section">
          <div className="section-header">
            <div className="section-badge">
              <Heart className="badge-icon" />
              <span className="badge-text">The Breakthrough</span>
            </div>
            <h2 className="section-title">
              Meeting Customers <span className="highlight">Where They Are</span>
            </h2>
          </div>
          
          <div ref={valuesRef} className="values-grid">
            <div className="value-card">
              <div className="value-icon">
                <Eye />
              </div>
              <h3>Customer Journey Reality</h3>
              <p>
                Since the rise of social media, it's been clear: customers don't move in straight lines. They search on Google, scroll Instagram, read Amazon reviews, and ask friends on Facebook—all before making a purchase.
              </p>
            </div>
            
            <div className="value-card">
              <div className="value-icon">
                <Zap />
              </div>
              <h3>Unified Strategy</h3>
              <p>
                The solution? Meet them where they are. Not with scattered campaigns, but with a unified strategy that connects every touchpoint. Not with guesswork, but with insights that guide the way.
              </p>
            </div>
            
            <div className="value-card">
              <div className="value-icon">
                <Shield />
              </div>
              <h3>Beyond Framework</h3>
              <p>
                Multi-funnel strategies that engage customers at every stage, cross-platform precision that turns attention into action, and data-backed decisions that replace assumptions with clarity.
              </p>
            </div>
          </div>
        </div>

        {/* Services Overview */}
        <div className="services-overview">
          <div className="section-header">
            <div className="section-badge">
              <BarChart3 className="badge-icon" />
              <span className="badge-text">Why We're Different</span>
            </div>
            <h2 className="section-title">
              We Don't Celebrate Likes — <span className="highlight">We Measure Impact</span>
            </h2>
          </div>
          
          <div ref={teamRef} className="services-grid">
            <div className="service-card">
              <div className="service-icon">
                <TrendingUp />
              </div>
              <h3>Transparent Reporting</h3>
              <p>Shows where every rupee goes, ensuring complete visibility into your marketing investment and its returns.</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon">
                <BarChart3 />
              </div>
              <h3>Ongoing Optimization</h3>
              <p>Adapts to what's working in real-time, continuously improving your campaigns for maximum effectiveness.</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon">
                <Target />
              </div>
              <h3>Scalable Strategies</h3>
              <p>Grow with your business, ensuring your marketing evolves as your company expands and develops.</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon">
                <Users />
              </div>
              <h3>Partnership Built on Trust</h3>
              <p>A relationship founded on trust and results, where your success is our primary measure of achievement.</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon">
                <Zap />
              </div>
              <h3>Performance Focus</h3>
              <p>Values outcomes over optics, ensuring every campaign strengthens your bottom line with measurable impact.</p>
            </div>
            
            <div className="service-card">
              <div className="service-icon">
                <Award />
              </div>
              <h3>Accountable Marketing</h3>
              <p>Every marketing investment should be accountable, every campaign should create measurable impact for your business.</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <div className="cta-content">
            <h2>Ready to go beyond?</h2>
            <p>Your growth, powered by performance and precision. We're not just another agency. We're a team that helps you turn marketing from an expense into an opportunity.</p>
            <div className="cta-buttons">
              <Link to="/consultation" className="cta-button primary">
                <Sparkles />
                Get Free Consultation
              </Link>
       
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;